import React, { useState, useEffect, useCallback } from 'react';
import { invoiceService } from '../../services/invoiceService';
import { memberService } from '../../services/memberService';
import { useNotifications } from '../common/NotificationSystem';
import { FormField, ModernInput, ModernSelect, ModernTextarea } from '../common/ModernForm';
import { getQuickFilters } from '../../utils/filterUtils';
import ProfessionalLoader from '../common/ProfessionalLoader';
import UnifiedHeader from '../common/UnifiedHeader';
import UnifiedStats from '../common/UnifiedStats';
import UnifiedFilters from '../common/UnifiedFilters';
import UnifiedActionPanel from '../common/UnifiedActionPanel';
import UnifiedTable from '../common/UnifiedTable';
import UnifiedEmptyState from '../common/UnifiedEmptyState';
import MemberSelectPanel from './MemberSelectPanel';
import './InvoiceManagement.css';

const InvoiceManagement = ({ userRole }) => {
  const { showSuccess, showError } = useNotifications();
  // RBAC permissions not currently used in this component

  const [invoices, setInvoices] = useState([]);
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    member_id: '',
    status: '',
    invoice_type: '',
    created_after: '',
    created_before: '',
    ordering: '-created_at'
  });
  const [searchInput, setSearchInput] = useState('');
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBulkCreateModal, setShowBulkCreateModal] = useState(false);
  const [isSyncingStatusFromXero, setIsSyncingStatusFromXero] = useState(false);
  const [stats, setStats] = useState({
    total_invoices: 0,
    draft_invoices: 0,
    sent_invoices: 0,
    paid_invoices: 0,
    overdue_invoices: 0,
    total_amount: 0,
    paid_amount: 0,
    outstanding_amount: 0
  });

  const isAdmin = userRole === 'admin' || userRole === 'Superuser' || userRole === 'Staff';

  const loadInvoices = useCallback(async (retryCount = 0) => {
    const maxRetries = 2;
    try {
      setIsLoading(true);
      setError(null);

      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value !== '')
      );

      const response = await invoiceService.getInvoices(cleanFilters);

      if (response.success && response.data != null) {
        // Support both raw array and paginated { results } from backend
        const raw = response.data;
        const list = Array.isArray(raw)
          ? raw
          : (raw?.results && Array.isArray(raw.results))
            ? raw.results
            : [];
        setInvoices(list);
      } else {
        const msg = response.message || 'Failed to load invoices';
        if (retryCount < maxRetries && (msg.includes('timed out') || msg.includes('interrupted'))) {
          await new Promise(r => setTimeout(r, 1500));
          return await loadInvoices(retryCount + 1);
        }
        setError(msg);
        showError(msg);
      }
    } catch (err) {
      if (retryCount < maxRetries) {
        await new Promise(r => setTimeout(r, 1500));
        return await loadInvoices(retryCount + 1);
      }
      setError('Error loading invoices. Please try again.');
      showError('Error loading invoices. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [filters, showError]);

  const loadMembers = useCallback(async () => {
    try {
      const response = await memberService.getMembers({ is_active: true });
      if (response.success) {
        setMembers(response.data || []);
      }
    } catch (err) {
    }
  }, []);

  const handleSyncStatusFromXero = useCallback(async () => {
    if (isSyncingStatusFromXero) return;
    setIsSyncingStatusFromXero(true);
    setError(null);
    try {
      const response = await invoiceService.syncStatusFromXero();
      if (response.success) {
        showSuccess(response.message || 'Syncing status from Xero. Refresh the list in a moment to see Paid/Sent updates.');
        setTimeout(() => loadInvoices(), 3000);
      } else {
        showError(response.error || response.message || 'Failed to sync status from Xero');
      }
    } catch (err) {
      showError('Failed to sync status from Xero. Please try again.');
    } finally {
      setIsSyncingStatusFromXero(false);
    }
  }, [isSyncingStatusFromXero, loadInvoices, showSuccess, showError]);

  // Load stats from invoice data instead of separate API call
  const loadStats = useCallback(() => {
    // Calculate stats from current invoices data
    const totalInvoices = invoices.length;
    const draftInvoices = invoices.filter(inv => inv.status === 'Draft').length;
    const sentInvoices = invoices.filter(inv => inv.status === 'Sent').length;
    const paidInvoices = invoices.filter(inv => inv.status === 'Paid').length;
    const overdueInvoices = invoices.filter(inv => inv.status === 'Overdue').length;

    const totalAmount = invoices.reduce((sum, inv) => sum + (parseFloat(inv.amount) || 0), 0);
    const paidAmount = invoices
      .filter(inv => inv.status === 'Paid')
      .reduce((sum, inv) => sum + (parseFloat(inv.amount) || 0), 0);
    const outstandingAmount = totalAmount - paidAmount;

    setStats({
      total_invoices: totalInvoices,
      draft_invoices: draftInvoices,
      sent_invoices: sentInvoices,
      paid_invoices: paidInvoices,
      overdue_invoices: overdueInvoices,
      total_amount: totalAmount,
      paid_amount: paidAmount,
      outstanding_amount: outstandingAmount
    });
  }, [invoices]);

  useEffect(() => {
    loadInvoices();
    loadMembers();
  }, [loadInvoices, loadMembers]);

  useEffect(() => {
    setSearchInput(filters.search || '');
  }, [filters.search]);

  // Update stats when invoices change
  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const handleCreateInvoice = async (invoiceData) => {
    setIsProcessing(true);
    try {
      // Format data according to backend requirements
      const formattedData = {
        member: parseInt(invoiceData.member_id),
        amount: parseFloat(invoiceData.amount).toFixed(2),
        invoice_type: invoiceData.invoice_type,
        issue_date: invoiceData.issue_date,
        due_date: invoiceData.due_date,
        financial_period: parseInt(invoiceData.financial_period),
        notes: invoiceData.description || ''
      };

      const response = await invoiceService.createInvoice(formattedData);
      if (response.success) {
        setSuccess('Invoice created successfully');
        showSuccess('Invoice created successfully');
        await loadInvoices(); // This will trigger loadStats automatically via useEffect
        setShowCreateModal(false);
      } else {
        setError(response.message || 'Failed to create invoice');
        showError(response.message || 'Failed to create invoice');
      }
    } catch (error) {
      // Error creating invoice
      setError('Failed to create invoice');
      showError('Failed to create invoice');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBulkCreateInvoices = async (bulkData) => {
    setIsProcessing(true);
    try {
      // Format data for bulk-create endpoint
      const formattedData = {
        member_ids: bulkData.member_ids.map(id => parseInt(id)),
        invoice_type: bulkData.invoice_type,
        amount: parseFloat(bulkData.amount).toFixed(2),
        issue_date: bulkData.issue_date,
        due_date: bulkData.due_date,
        financial_period: parseInt(bulkData.financial_period),
        notes: bulkData.description || ''
      };
      const response = await invoiceService.bulkCreateInvoices(formattedData);
      if (response.success) {
        setSuccess(`${bulkData.member_ids.length} invoices created successfully`);
        showSuccess(`${bulkData.member_ids.length} invoices created successfully`);
        await loadInvoices(); // This will trigger loadStats automatically via useEffect
        setShowBulkCreateModal(false);
      } else {
        setError(response.message || 'Failed to create invoices');
        showError(response.message || 'Failed to create invoices');
      }
    } catch (error) {
      // Error creating invoices
      setError('Failed to create invoices');
      showError('Failed to create invoices');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSendToXero = async (invoiceId) => {
    setIsProcessing(true);
    try {
      const response = await invoiceService.sendInvoiceToXero(invoiceId);
      if (response.success) {
        const successMessage = response.data?.message || response.message || 'Invoice sent to Xero successfully';
        setSuccess(successMessage);
        showSuccess(successMessage);
        await loadInvoices(); // This will trigger loadStats automatically via useEffect
      } else {
        // Handle specific error messages from the API
        // Prioritize: message > error > validation_errors[0] > generic
        let errorMessage = 'Failed to send invoice to Xero';

        if (response.message) {
          errorMessage = response.message;
        } else if (response.error) {
          errorMessage = response.error;
        } else if (response.data?.message) {
          errorMessage = response.data.message;
        } else if (response.data?.error) {
          errorMessage = response.data.error;
        } else if (response.data?.validation_errors && response.data.validation_errors.length > 0) {
          errorMessage = response.data.validation_errors[0];
        }

        setError(errorMessage);
        showError(errorMessage);

        // Refresh invoice list to reflect any status changes (e.g., if status was updated to "Failed")
        await loadInvoices();
      }
    } catch (error) {
      // Error sending invoice to Xero
      let errorMessage = 'Failed to send invoice to Xero';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
      showError(errorMessage);

      // Refresh invoice list to reflect any status changes
      await loadInvoices();
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSendEmail = async (invoiceId) => {
    setIsProcessing(true);
    try {
      const response = await invoiceService.sendInvoiceEmail(invoiceId);
      if (response.success) {
        setSuccess(response.message || 'Invoice sent via email successfully');
        showSuccess(response.message || 'Invoice sent via email successfully');
        await loadInvoices(); // This will trigger loadStats automatically via useEffect
      } else {
        // Handle specific error messages from the API
        const errorMessage = response.error || response.message || 'Failed to send invoice email';
        setError(errorMessage);
        showError(errorMessage);

        // Refresh invoice list to reflect any status changes
        await loadInvoices();
      }
    } catch (error) {
      // Error sending invoice email
      const errorMessage = error.response?.data?.error || error.message || 'Failed to send invoice email';
      setError(errorMessage);
      showError(errorMessage);

      // Refresh invoice list to reflect any status changes
      await loadInvoices();
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) {
      return;
    }

    setIsProcessing(true);
    try {
      const response = await invoiceService.deleteInvoice(invoiceId);
      if (response.success) {
        setSuccess('Invoice deleted successfully');
        showSuccess('Invoice deleted successfully');
        await loadInvoices(); // This will trigger loadStats automatically via useEffect
      } else {
        setError(response.message || 'Failed to delete invoice');
        showError(response.message || 'Failed to delete invoice');
      }
    } catch (error) {
      // Error deleting invoice
      setError('Failed to delete invoice');
      showError('Failed to delete invoice');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedInvoices.length === 0) {
      setError('Please select invoices to perform action');
      showError('Please select invoices to perform action');
      return;
    }

    setIsProcessing(true);
    try {
      const isSingle = selectedInvoices.length === 1;
      const countText = isSingle ? '1 invoice' : `${selectedInvoices.length} invoices`;

      switch (action) {
        case 'delete':
          if (!window.confirm(`Are you sure you want to delete ${countText}?`)) {
            return;
          }
          for (const invoiceId of selectedInvoices) {
            await invoiceService.deleteInvoice(invoiceId);
          }
          const deleteMessage = isSingle ? 'Invoice deleted successfully' : `${countText} deleted successfully`;
          setSuccess(deleteMessage);
          showSuccess(deleteMessage);
          break;
        case 'send_xero':
          const response = await invoiceService.bulkSendInvoices(selectedInvoices);
          if (response.success) {
            const responseData = response.data || response;
            const { queued, skipped } = responseData;

            if (queued > 0) {
              const queuedMessage = isSingle
                ? 'Invoice queued for Xero sync. Refresh in a moment to see the updated status.'
                : `${queued} invoice(s) queued for Xero sync. Refresh in a few minutes to see updated statuses.`;
              setSuccess(queuedMessage);
              showSuccess(queuedMessage);
            }

            if (skipped && skipped.length > 0) {
              const skipMessage = `${skipped.length} invoice(s) could not be sent: ${skipped[0]?.error || 'validation error'}`;
              setError(skipMessage);
              showError(skipMessage);
            }

            if (!queued && (!skipped || skipped.length === 0)) {
              showSuccess(responseData.message || 'No eligible invoices to sync.');
            }
          } else {
            let errorMessage = isSingle ? 'Failed to send invoice to Xero' : 'Failed to send invoices to Xero';
            if (response.message) {
              errorMessage = response.message;
            } else if (response.error) {
              errorMessage = response.error;
            } else if (response.data?.message) {
              errorMessage = response.data.message;
            } else if (response.data?.validation_errors && response.data.validation_errors.length > 0) {
              errorMessage = response.data.validation_errors[0];
            }
            setError(errorMessage);
            showError(errorMessage);
            return;
          }
          break;
        default:
          setError('Invalid action');
          showError('Invalid action');
          return;
      }

      setSelectedInvoices([]);
      await loadInvoices();
      await loadStats();
    } catch (error) {
      // Error performing action
      const errorMessage = selectedInvoices.length === 1 ? 'Failed to perform action' : 'Failed to perform bulk action';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleInvoiceSelect = (invoiceId, isSelected) => {
    setSelectedInvoices(prev =>
      isSelected
        ? [...prev, invoiceId]
        : prev.filter(id => id !== invoiceId)
    );
  };

  const handleSelectAll = (isSelected) => {
    setSelectedInvoices(isSelected ? invoices.map(invoice => invoice.id) : []);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSearchInputChange = useCallback((value) => {
    setSearchInput(value);
  }, []);

  const handleSearchSubmit = useCallback(() => {
    setFilters(prev => ({ ...prev, search: searchInput }));
  }, [searchInput]);

  const clearFilters = () => {
    setFilters({
      search: '',
      member_id: '',
      status: '',
      invoice_type: '',
      created_after: '',
      created_before: '',
      ordering: '-created_at'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-AU');
  };

  const getStatusClass = (status) => {
    switch (status.toLowerCase()) {
      case 'draft': return 'status-draft';
      case 'sent': return 'status-sent';
      case 'paid': return 'status-paid';
      case 'overdue': return 'status-overdue';
      case 'cancelled': return 'status-cancelled';
      default: return 'status-unknown';
    }
  };

  const getXeroStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'synced': return 'xero-status-synced';
      case 'not_synced': return 'xero-status-not-synced';
      case 'failed': return 'xero-status-failed';
      case 'pending': return 'xero-status-pending';
      default: return 'xero-status-default';
    }
  };


  if (isLoading) {
    return (
      <div className="invoice-management">
        <div className="loading-container">
          <ProfessionalLoader message="Loading invoices..." size="large" variant="default" />
        </div>
      </div>
    );
  }

  return (
    <div className="invoice-management">
      {/* Invoices Management Section */}
      <div className="invoices-management-container">
        {/* Unified Header */}
        <UnifiedHeader
          title="Invoice Management"
          subtitle="Manage and organize invoice records"
          icon="📄"
          count={invoices.length}
          countLabel="Total Invoices"
          selectedCount={selectedInvoices.length}
          selectedLabel="Selected"
        >
          {isAdmin && (
            <button
              className="btn btn-primary"
              onClick={() => setShowCreateModal(true)}
              disabled={isProcessing}
            >
              ➕ Create Invoice
            </button>
          )}
        </UnifiedHeader>

        {/* Unified Statistics */}
        <UnifiedStats
          stats={[
            { title: '📄 Total Invoices', value: stats.total_invoices || invoices.length },
            { title: '💰 Total Amount', value: formatCurrency(stats.total_amount || invoices.reduce((sum, inv) => sum + (inv.amount || 0), 0)) },
            { title: '✅ Paid Invoices', value: stats.paid_invoices || invoices.filter(inv => inv.status === 'paid').length },
            { title: '⏳ Outstanding Amount', value: formatCurrency(stats.outstanding_amount || 0) }
          ]}
        />

        {/* Unified Filters */}
        <UnifiedFilters
          searchValue={searchInput}
          onSearchChange={handleSearchInputChange}
          onSearchSubmit={handleSearchSubmit}
          quickFilters={getQuickFilters('invoices', filters)}
          onQuickFilterClick={(filterKey) => {
            if (filterKey === 'all') {
              clearFilters();
            } else if (filterKey === 'renewal' || filterKey === 'new_member') {
              // These must stay in step with INVOICE_TYPE_CHOICES on the Invoice
              // model. The previous button sent invoice_type=membership, which is
              // not a valid type, so it matched no invoices and emptied the list.
              handleFilterChange('invoice_type', filters.invoice_type === filterKey ? '' : filterKey);
            } else {
              handleFilterChange('status', filters.status === filterKey ? '' : filterKey);
            }
          }}
        />

        {/* Unified Action Panel */}
        <UnifiedActionPanel
          selectedCount={selectedInvoices.length}
          totalCount={invoices.length}
          selectedItemsLabel="invoices"
          onSelectAll={handleSelectAll}
          onClearSelection={() => setSelectedInvoices([])}
          selectedAction=""
          onActionChange={(action) => {
            if (action) {
              handleBulkAction(action);
            }
          }}
          onExecuteAction={() => {
            // Handle execute action if needed
          }}
          isProcessing={isProcessing}
          actionOptions={[
            { value: 'send_xero', label: '📤 Send to Xero', disabled: false },
            { value: 'delete', label: '🗑️ Delete Invoices', disabled: false }
          ]}
        >
          {isAdmin && (
            <div className="create-actions">
              <button
                className="btn btn-primary"
                onClick={() => setShowCreateModal(true)}
                disabled={isProcessing}
              >
                ➕ Create Invoice
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setShowBulkCreateModal(true)}
                disabled={isProcessing}
              >
                📋 Bulk Create
              </button>
            </div>
          )}
        </UnifiedActionPanel>

        {/* Error/Success Messages */}
        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        {success && (
          <div className="alert alert-success">
            {success}
          </div>
        )}

        {/* Invoices Table */}
        {invoices.length > 0 ? (
          <UnifiedTable
            columns={[
              { key: 'invoice_number', header: 'Invoice #', className: 'unified-table-select-header' },
              { key: 'member', header: 'Member' },
              { key: 'amount', header: 'Amount' },
              { key: 'type', header: 'Type' },
              { key: 'status', header: 'Status' },
              { key: 'issue_date', header: 'Issue Date' },
              { key: 'due_date', header: 'Due Date' },
              { key: 'xero_status', header: 'Xero Status' },
              { key: 'created_at', header: 'Created' },
              { key: 'actions', header: 'Actions' }
            ]}
            data={invoices}
            onRowClick={(invoice) => {
              // Handle row click - could open view modal or edit
              console.log('Invoice clicked:', invoice);
            }}
            onSelectRow={handleInvoiceSelect}
            selectedRows={selectedInvoices}
            renderCell={(column, invoice) => {
              switch (column.key) {
                case 'invoice_number':
                  return <span className="unified-invoice-number">{invoice.invoice_number}</span>;
                case 'member':
                  return (
                    <div className="unified-member-info">
                      <div className="unified-member-name">{invoice.member_name || 'Unknown'}</div>
                      <div className="unified-member-id">{invoice.member_id || ''}</div>
                    </div>
                  );
                case 'amount':
                  return <span className="unified-amount">{formatCurrency(invoice.amount)}</span>;
                case 'type':
                  return (
                    <span className="unified-invoice-type-badge">
                      {invoice.invoice_type_display || invoice.invoice_type}
                    </span>
                  );
                case 'status':
                  return (
                    <span className={`unified-status-badge ${getStatusClass(invoice.status)}`}>
                      {invoice.status_display || invoice.status}
                    </span>
                  );
                case 'issue_date':
                  return formatDate(invoice.issue_date);
                case 'due_date':
                  return formatDate(invoice.due_date);
                case 'xero_status':
                  return (
                    <span className={`unified-xero-status-badge ${getXeroStatusClass(invoice.xero_sync_status)}`}>
                      {invoice.xero_sync_status_display || invoice.xero_sync_status}
                    </span>
                  );
                case 'created_at':
                  return formatDate(invoice.created_at);
                case 'actions':
                  return (
                    <div className="action-buttons">
                      <button
                        className="btn btn-sm btn-info"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSendEmail(invoice.id);
                        }}
                        disabled={isProcessing || !invoice.member?.primary_contact_email}
                        title={invoice.member?.primary_contact_email ? 'Send invoice as PDF via email' : 'Member does not have a primary contact email'}
                      >
                        📧
                      </button>
                      {invoice.xero_sync_status !== 'synced' && invoice.xero_sync_status !== 'Synced' && (
                        <button
                          className="btn btn-sm btn-outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSendToXero(invoice.id);
                          }}
                          disabled={isProcessing}
                          title="Send to Xero"
                        >
                          📤
                        </button>
                      )}
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteInvoice(invoice.id);
                        }}
                        disabled={isProcessing}
                        title="Delete Invoice"
                      >
                        🗑️
                      </button>
                    </div>
                  );
                default:
                  return invoice[column.key];
              }
            }}
          />
        ) : (
          <UnifiedEmptyState
            icon="📄"
            title="No invoices found"
            description="Try adjusting your search criteria or create a new invoice."
            actions={[
              ...(isAdmin ? [{
                label: 'Create First Invoice',
                icon: '➕',
                onClick: () => setShowCreateModal(true),
                variant: 'primary'
              }] : [])
            ]}
          />
        )}
      </div>

      {/* Create Invoice Modal */}
      {showCreateModal && (
        <InvoiceCreateModal
          members={members}
          onSubmit={handleCreateInvoice}
          onClose={() => setShowCreateModal(false)}
        />
      )}

      {/* Bulk Create Modal */}
      {showBulkCreateModal && (
        <InvoiceBulkCreateModal
          members={members}
          onSubmit={handleBulkCreateInvoices}
          onClose={() => setShowBulkCreateModal(false)}
        />
      )}
    </div>
  );
};

// Invoice Create Modal Component
const InvoiceCreateModal = ({ members, onSubmit, onClose }) => {
  const [formData, setFormData] = useState({
    member_id: '',
    amount: '',
    invoice_type: 'renewal',
    description: '',
    due_date: '',
    issue_date: '',
    financial_period: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [financialPeriods, setFinancialPeriods] = useState([]);

  // Load financial periods
  useEffect(() => {
    const loadFinancialPeriods = async () => {
      try {
        const response = await invoiceService.getFinancialPeriods();
        if (response.success) {
          setFinancialPeriods(response.data || []);
        }
      } catch (error) {
        // Failed to load financial periods
      }
    };
    loadFinancialPeriods();
  }, []);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.member_id) {
      newErrors.member_id = 'Please select a member';
    }

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Please enter a valid amount';
    }

    if (!formData.invoice_type) {
      newErrors.invoice_type = 'Please select an invoice type';
    }

    if (!formData.issue_date) {
      newErrors.issue_date = 'Please select an issue date';
    }

    if (!formData.due_date) {
      newErrors.due_date = 'Please select a due date';
    }

    if (!formData.financial_period) {
      newErrors.financial_period = 'Please select a financial period';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content invoice-modal large">
        <div className="modal-header">
          <h3>Create Invoice</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <MemberSelectPanel
            members={members}
            value={formData.member_id}
            onChange={(memberId) => handleChange('member_id', memberId)}
            error={errors.member_id}
          />

          <FormField label="Amount" required error={errors.amount}>
            <ModernInput
              type="number"
              value={formData.amount}
              onChange={(e) => handleChange('amount', e.target.value)}
              step="0.01"
              min="0"
              placeholder="Enter amount"
              error={!!errors.amount}
            />
          </FormField>

          <FormField label="Type" required error={errors.invoice_type}>
            <ModernSelect
              value={formData.invoice_type}
              onChange={(e) => handleChange('invoice_type', e.target.value)}
              error={!!errors.invoice_type}
            >
              <option value="renewal">Annual Renewal</option>
              <option value="new_member">New Member</option>
              <option value="late_fee">Late Fee</option>
              <option value="other">Other</option>
            </ModernSelect>
          </FormField>

          <FormField label="Description">
            <ModernTextarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={3}
              placeholder="Enter description (optional)"
            />
          </FormField>

          <FormField label="Issue Date" required error={errors.issue_date}>
            <ModernInput
              type="date"
              value={formData.issue_date}
              onChange={(e) => handleChange('issue_date', e.target.value)}
              error={!!errors.issue_date}
            />
          </FormField>

          <FormField label="Due Date" required error={errors.due_date}>
            <ModernInput
              type="date"
              value={formData.due_date}
              onChange={(e) => handleChange('due_date', e.target.value)}
              error={!!errors.due_date}
            />
          </FormField>

          <FormField label="Financial Period" required error={errors.financial_period}>
            <ModernSelect
              value={formData.financial_period}
              onChange={(e) => handleChange('financial_period', e.target.value)}
              error={!!errors.financial_period}
            >
              <option value="">Select Financial Period</option>
              {financialPeriods.map(period => (
                <option key={period.id} value={period.id}>
                  {period.name} ({period.start_date} - {period.end_date})
                </option>
              ))}
            </ModernSelect>
          </FormField>

          <div className="modal-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Create Invoice'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Invoice Bulk Create Modal Component
const InvoiceBulkCreateModal = ({ members, onSubmit, onClose }) => {
  const [formData, setFormData] = useState({
    member_ids: [],
    amount: '',
    invoice_type: 'renewal',
    description: '',
    due_date: '',
    issue_date: '',
    financial_period: ''
  });
  const [financialPeriods, setFinancialPeriods] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load financial periods
  useEffect(() => {
    const loadFinancialPeriods = async () => {
      try {
        const response = await invoiceService.getFinancialPeriods();
        if (response.success) {
          setFinancialPeriods(response.data || []);
        }
      } catch (error) {
        // Failed to load financial periods
      }
    };
    loadFinancialPeriods();
  }, []);

  const validateForm = () => {
    const newErrors = {};

    if (formData.member_ids.length === 0) {
      newErrors.member_ids = 'Please select at least one member';
    }

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Please enter a valid amount';
    }

    if (!formData.invoice_type) {
      newErrors.invoice_type = 'Please select an invoice type';
    }

    if (!formData.issue_date) {
      newErrors.issue_date = 'Please select an issue date';
    }

    if (!formData.due_date) {
      newErrors.due_date = 'Please select a due date';
    }

    if (!formData.financial_period) {
      newErrors.financial_period = 'Please select a financial period';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleMemberIdsChange = (memberIds) => {
    handleChange('member_ids', memberIds);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content invoice-modal large">
        <div className="modal-header">
          <h3>Bulk Create Invoices</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <MemberSelectPanel
            members={members}
            multiple
            value={formData.member_ids}
            onChange={handleMemberIdsChange}
            error={errors.member_ids}
          />

          <FormField label="Amount" required error={errors.amount}>
            <ModernInput
              type="number"
              value={formData.amount}
              onChange={(e) => handleChange('amount', e.target.value)}
              step="0.01"
              min="0"
              placeholder="Enter amount"
              error={!!errors.amount}
            />
          </FormField>

          <FormField label="Type" required error={errors.invoice_type}>
            <ModernSelect
              value={formData.invoice_type}
              onChange={(e) => handleChange('invoice_type', e.target.value)}
              error={!!errors.invoice_type}
            >
              <option value="renewal">Annual Renewal</option>
              <option value="new_member">New Member</option>
              <option value="late_fee">Late Fee</option>
              <option value="other">Other</option>
            </ModernSelect>
          </FormField>

          <FormField label="Description">
            <ModernTextarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={3}
              placeholder="Enter description (optional)"
            />
          </FormField>

          <FormField label="Issue Date" required error={errors.issue_date}>
            <ModernInput
              type="date"
              value={formData.issue_date}
              onChange={(e) => handleChange('issue_date', e.target.value)}
              error={!!errors.issue_date}
            />
          </FormField>

          <FormField label="Due Date" required error={errors.due_date}>
            <ModernInput
              type="date"
              value={formData.due_date}
              onChange={(e) => handleChange('due_date', e.target.value)}
              error={!!errors.due_date}
            />
          </FormField>

          <FormField label="Financial Period" required error={errors.financial_period}>
            <ModernSelect
              value={formData.financial_period}
              onChange={(e) => handleChange('financial_period', e.target.value)}
              error={!!errors.financial_period}
            >
              <option value="">Select Financial Period</option>
              {financialPeriods.map(period => (
                <option key={period.id} value={period.id}>
                  {period.name} ({period.start_date} - {period.end_date})
                </option>
              ))}
            </ModernSelect>
          </FormField>

          <div className="modal-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : `Create ${formData.member_ids.length} Invoices`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InvoiceManagement;